import { Rol } from "@copi/shared";
import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import {
  ROUTE_ACTIVAR,
  ROUTE_ADMIN_ACADEMICO,
  ROUTE_ADMIN_ESTUDIANTES,
  ROUTE_ADMIN_CATCH_ALL,
  ROUTE_ADMIN_FERIADOS,
  ROUTE_ADMIN_INSTITUCIONES,
  ROUTE_ADMIN_PERSONALIZACION,
  ROUTE_ADMIN_USUARIOS,
  ROUTE_DEV_STYLEGUIDE,
  ROUTE_BANDEJA_ASESORIA,
  ROUTE_BANDEJA_DECANATO,
  ROUTE_BANDEJA_JURADO,
  ROUTE_DASHBOARD,
  ROUTE_EXPEDIENTE_DETALLE,
  ROUTE_EXPEDIENTE_PROPONER_ASESORIA,
  ROUTE_EXPEDIENTE_PROYECTO_LATEX,
  ROUTE_HOME,
  ROUTE_LOGIN,
  ROUTE_MIS_EXPEDIENTES,
  ROUTE_MIS_SOLICITUDES,
  ROUTE_PERFIL,
  ROUTE_REPORTES_DECANATO,
  ROUTE_SOLICITUD_NUEVA,
  ROUTE_VERIFICAR_QR,
} from "./paths";

import { AppShell } from "@/components/layout/AppShell";
import { ProtectedRoute } from "@/components/layout/ProtectedRoute";
import { useAuth } from "@/features/auth/auth.context";
import { PlaceholderPage } from "@/features/dashboard/PlaceholderPage";

const LoginPage = lazy(() => import("@/features/auth/pages/LoginPage"));
const ActivarCuentaPage = lazy(() => import("@/features/auth/pages/ActivarCuentaPage"));
const LandingPage = lazy(() => import("@/features/landing/LandingPage"));
const DashboardPage = lazy(() => import("@/features/dashboard/DashboardPage"));
const ExpedienteDetallePage = lazy(
  () => import("@/features/expedientes/pages/ExpedienteDetallePage"),
);
const MisExpedientesPage = lazy(() => import("@/features/expedientes/pages/MisExpedientesPage"));
const NuevaSolicitudPage = lazy(() => import("@/features/solicitudes/pages/NuevaSolicitudPage"));
const MisSolicitudesPage = lazy(() => import("@/features/solicitudes/pages/MisSolicitudesPage"));
const BandejaDecanatoPage = lazy(() => import("@/features/decanato/pages/BandejaDecanatoPage"));
const MisEstudiantesPage = lazy(() => import("@/features/asesoria/pages/MisEstudiantesPage"));
const MisAsignacionesPage = lazy(() => import("@/features/jurado/pages/MisAsignacionesPage"));
const ReportesPage = lazy(() => import("@/features/reportes/ReportesPage"));
const PerfilPage = lazy(() => import("@/features/perfil/PerfilPage"));
const FeriadosPage = lazy(() => import("@/features/admin/FeriadosPage"));
const InstitucionesPage = lazy(() => import("@/features/admin/InstitucionesPage"));
const PersonalizacionPage = lazy(() => import("@/features/admin/PersonalizacionPage"));
const DocentesCapacidadesPage = lazy(() => import("@/features/admin/DocentesCapacidadesPage"));
const AdminAcademicoPage = lazy(() => import("@/features/admin/AdminAcademicoPage"));
const AdminEstudiantesPage = lazy(() => import("@/features/admin/AdminEstudiantesPage"));
const StyleguidePage = lazy(() => import("@/features/dev/StyleguidePage"));
const ProponerAsesoriaPage = lazy(
  () => import("@/features/aceptacion-asesor/ProponerAsesoriaPage"),
);
const VerificarPage = lazy(() => import("@/features/verificacion/VerificarPage"));
const ProyectoLatexPage = lazy(() => import("@/features/proyecto-latex/pages/ProyectoLatexPage"));

function SuspenseFallback() {
  return (
    <div
      className="flex min-h-screen items-center justify-center bg-background"
      role="status"
      aria-live="polite"
    >
      <span className="text-sm text-muted-foreground">Cargando…</span>
    </div>
  );
}

/**
 * Despacha la raíz `/` (ADR 051): un visitante sin sesión ve la landing pública;
 * un usuario autenticado salta a su dashboard (`/inicio`). Mientras se resuelve
 * la sesión se muestra el splash (evita un flash de landing para usuarios ya
 * logueados). La landing es pública: no va envuelta en `ProtectedRoute`.
 */
function HomeRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <SuspenseFallback />;
  if (isAuthenticated) return <Navigate to={ROUTE_DASHBOARD} replace />;
  return <LandingPage />;
}

export function Router() {
  return (
    <Suspense fallback={<SuspenseFallback />}>
      <Routes>
        {/* Raíz pública (ADR 051): landing para visitantes, dashboard para
            sesiones autenticadas. Sin AppShell ni guard (la gateación la hace
            HomeRoute). */}
        <Route path={ROUTE_HOME} element={<HomeRoute />} />
        <Route path={ROUTE_LOGIN} element={<LoginPage />} />
        {/* Activación de cuenta por invitación (ADR 053). Pública, sin AppShell:
            el invitado aún no tiene sesión. */}
        <Route path={ROUTE_ACTIVAR} element={<ActivarCuentaPage />} />
        {/* Landing público de verificación QR (Fase 6 — ADR 007). Sin AppShell ni guards. */}
        <Route path={ROUTE_VERIFICAR_QR} element={<VerificarPage />} />

        {/* Rutas protegidas — todas viven dentro del AppShell */}
        <Route
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          {/* Dashboard autenticado en `/inicio` (ADR 051); la raíz la sirve HomeRoute. */}
          <Route path={ROUTE_DASHBOARD} element={<DashboardPage />} />
          <Route path={ROUTE_MIS_EXPEDIENTES} element={<MisExpedientesPage />} />
          <Route path={ROUTE_EXPEDIENTE_DETALLE} element={<ExpedienteDetallePage />} />
          <Route
            path={ROUTE_EXPEDIENTE_PROYECTO_LATEX}
            element={
              // ADR 027 + post-mortem 2026-05-27: el docente (asesor o jurado)
              // accede al editor en modo "leer y comentar" (puedeEditar=false en
              // `esRolDeRevision`; puedeComentar=true). Compuerta gruesa por rol;
              // el acceso fino (asesor designado / miembro del jurado de ESTE
              // expediente) lo resuelve el backend.
              <ProtectedRoute
                roles={[Rol.ESTUDIANTE, Rol.BACHILLER, Rol.DOCENTE, Rol.DECANATO, Rol.ADMIN]}
              >
                <ProyectoLatexPage />
              </ProtectedRoute>
            }
          />
          {/* ADR 020: /solicitudes es solo para estudiantes (historial +
              subsanar). DECANATO/ADMIN usan /decanato como bandeja única. */}
          <Route
            path={ROUTE_MIS_SOLICITUDES}
            element={
              <ProtectedRoute roles={[Rol.ESTUDIANTE, Rol.BACHILLER]}>
                <MisSolicitudesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTE_SOLICITUD_NUEVA}
            element={
              <ProtectedRoute roles={[Rol.ESTUDIANTE, Rol.BACHILLER]}>
                <NuevaSolicitudPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTE_EXPEDIENTE_PROPONER_ASESORIA}
            element={
              <ProtectedRoute roles={[Rol.ESTUDIANTE, Rol.BACHILLER]}>
                <ProponerAsesoriaPage />
              </ProtectedRoute>
            }
          />
          {/* ADR 036: la ruta /presentar-al-decanato se eliminó. La aceptación
              del asesor presenta la solicitud al Decanato automáticamente. */}
          {/* ADR 020: /asesoria/propuestas fue eliminada. La sección de
              propuestas recibidas vive ahora dentro de /asesoria como
              bloque superior — más coherente con el flujo del asesor. */}
          <Route
            path={ROUTE_BANDEJA_DECANATO}
            element={
              <ProtectedRoute roles={[Rol.DECANATO, Rol.ADMIN]}>
                <BandejaDecanatoPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTE_REPORTES_DECANATO}
            element={
              <ProtectedRoute roles={[Rol.DECANATO, Rol.ADMIN]}>
                <ReportesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTE_BANDEJA_ASESORIA}
            element={
              // ADR 054 — gateado por CAPACIDAD de asesorar (ya no por rol ASESOR).
              <ProtectedRoute capacidades={["asesor"]}>
                <MisEstudiantesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTE_BANDEJA_JURADO}
            element={
              // ADR 054 — gateado por CAPACIDAD de ser jurado (ya no por rol JURADO).
              <ProtectedRoute capacidades={["jurado"]}>
                <MisAsignacionesPage />
              </ProtectedRoute>
            }
          />
          {/* ADR 020: la ruta /repositorio con PlaceholderPage fue
              eliminada. El rol REPOSITORIO ve `RepositorioDashboard` en /.
              Si el ADMIN necesita gestión institucional del repositorio,
              usará /expedientes con filtros. */}
          <Route path={ROUTE_PERFIL} element={<PerfilPage />} />
          <Route
            path={ROUTE_ADMIN_FERIADOS}
            element={
              <ProtectedRoute roles={[Rol.ADMIN, Rol.DECANATO]}>
                <FeriadosPage />
              </ProtectedRoute>
            }
          />
          {/* ADR 045 — alta de instituciones, sólo plataforma. */}
          <Route
            path={ROUTE_ADMIN_INSTITUCIONES}
            element={
              <ProtectedRoute roles={[Rol.SUPER_ADMIN]}>
                <InstitucionesPage />
              </ProtectedRoute>
            }
          />
          {/* ADR 047 — personalización self-service del tenant, sólo ADMIN. */}
          <Route
            path={ROUTE_ADMIN_PERSONALIZACION}
            element={
              <ProtectedRoute roles={[Rol.ADMIN]}>
                <PersonalizacionPage />
              </ProtectedRoute>
            }
          />
          {/* ADR 055 — estructura académica del tenant (facultades y escuelas/EAPs), sólo ADMIN. */}
          <Route
            path={ROUTE_ADMIN_ACADEMICO}
            element={
              <ProtectedRoute roles={[Rol.ADMIN]}>
                <AdminAcademicoPage />
              </ProtectedRoute>
            }
          />
          {/* ADR 054 — gestión de docentes y sus capacidades (asesor/jurado), sólo ADMIN. */}
          <Route
            path={ROUTE_ADMIN_USUARIOS}
            element={
              <ProtectedRoute roles={[Rol.ADMIN]}>
                <DocentesCapacidadesPage />
              </ProtectedRoute>
            }
          />
          {/* ADR 055 — alta individual de estudiantes, sólo ADMIN. */}
          <Route
            path={ROUTE_ADMIN_ESTUDIANTES}
            element={
              <ProtectedRoute roles={[Rol.ADMIN]}>
                <AdminEstudiantesPage />
              </ProtectedRoute>
            }
          />
          {/* Styleguide de componentes — SÓLO en desarrollo (no existe en prod). */}
          {import.meta.env.DEV ? (
            <Route
              path={ROUTE_DEV_STYLEGUIDE}
              element={
                <ProtectedRoute>
                  <StyleguidePage />
                </ProtectedRoute>
              }
            />
          ) : null}
          <Route
            path={ROUTE_ADMIN_CATCH_ALL}
            element={
              <ProtectedRoute roles={[Rol.ADMIN]}>
                <PlaceholderPage
                  titulo="Administración"
                  descripcion="Gestión de usuarios y configuración del sistema."
                  proximaFase="Fase 0 (completado, faltan páginas)"
                />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Cualquier URL desconocida → raíz, que despacha según haya sesión. */}
        <Route path="*" element={<Navigate to={ROUTE_HOME} replace />} />
      </Routes>
    </Suspense>
  );
}
